import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, catchError, tap, throwError, timeout } from 'rxjs';
import { BaseRequest } from 'src/app/models/requests/baseRequest';
import { PortalBaseRequest } from 'src/app/models/requests/portalBaseRequest';
import { SearchRequest } from 'src/app/models/requests/searchRequest';
import { environment } from 'src/environments/environment';
import { LocalizationService } from './localization.service';
import { ConfigService } from './config/config.service';

declare var window: any;

@Injectable({
  providedIn: 'root',
})
export class DataService {
  constructor(
    private http: HttpClient,
    @Inject(DOCUMENT) private document: Document,
    private configService: ConfigService
  ) {}

  //cultureCode: string = 'he-IL';
  shareBtnData: any;
  currentPageNodeId: number = 0;
  generalSettingsData: any;
  private generalSettingsData$ = new BehaviorSubject<any>(null);
  updateGeneralSettingsData$: BehaviorSubject<any> = this.generalSettingsData$;
  addToCalendarPopupData: any;
  private addToCalendarPopupData$ = new BehaviorSubject<any>(null);
  updateAddToCalendarPopupData$: BehaviorSubject<any> =
    this.addToCalendarPopupData$;

  // setCultureCode(cultureCode: string): void {
  //   this.cultureCode = cultureCode;
  // }

  getCultureCode(): string {
    //return this.cultureCode;
    return LocalizationService.getCurrentCultureCode();
  }

  getLayoutData(source: string, request: BaseRequest) {
    return this.http
      .post(source, request, { headers: this.getAdditionalHeaders() })
      .pipe(
        tap((response: any) => {
          response = JSON.parse(response);
          this.shareBtnData = response.shareBtnData;
          this.generalSettingsData = response.portalGeneralSettingsData; 
          this.updateGeneralSettingsData$.next(
            response.portalGeneralSettingsData
          );
          this.updateAddToCalendarPopupData$.next(
            response.generalPopup.addToCalendarPopupData
          );
        }),
        catchError(this.handleError)
      );
  }

  getPortalRegistrationPageData(source: string, request: BaseRequest) {
    return this.http.post(source, request).pipe(
      tap((res: any) => {
        res = JSON.parse(res);
      }),
      catchError(this.handleError)
    );
  }

  getDataByToken(
    source: string,
    request: any,
    useConfigTimeout: boolean = true
  ) {
    let timeoutValue = useConfigTimeout
      ? this.configService.timeout * 1000
      : 360000;
    return this.http
      .post(source, request, { headers: this.getAdditionalHeaders() })
      .pipe(
        timeout(timeoutValue),
        tap((res: any) => res),
        catchError(this.handleError)
      );
  }

  private handleError(error: any) {
    return throwError(error.error || 'Server error');
  }

  getFilteredStudentDataTypes(dataType: any): any[] {
    return this.generalSettingsData.studentDataTypeItems.filter(
      (item: any) => item.studentDataTypeItemData.key == dataType
    );
  }

  openNewWindow(url: string) {
    // Use window.open() to open the URL in a new window
    window.open(url, '_blank');
  }

  getCourseMoodleUrl(courseIdNumberData: string) {
    var source =
      environment.apiUrl +
      '/umbraco/Surface/MainPortalSurface/GetCourseMoodleUrl';
    var request: any = {
      courseIdNumber: courseIdNumberData,
    };
    return this.http
      .post(source, request, { headers: this.getAdditionalHeaders() })
      .pipe(
        tap((res: any) => {
          window.open(res.courseMoodleUrl, '_blank');
        }),
        catchError(this.handleError)
      );
  }

  getPortalPageData(source: string, request: SearchRequest) {
    return this.http
      .post(source, request, {
        responseType: 'text',
        headers: this.getAdditionalHeaders(),
      })
      .pipe(
        tap((res: any) => JSON.parse(res)),
        catchError(this.handleError)
      );
  }

  getBillingTableData() {
    var source =
      environment.apiUrl +
      '/Portal/PortalAngularBillingInfo/GetBillingTableData';
    var cultureCode = this.getCultureCode();
    return this.http
      .post(source, { cultureCode }, { headers: this.getAdditionalHeaders() })
      .pipe(
        timeout(this.configService.timeout * 1000),
        tap((res: any) => res),
        catchError(this.handleError)
      );
  }

  getPortalCardSearchResult(source: string, request: SearchRequest) {
    return this.http
      .post(source, request, {
        responseType: 'text',
        headers: this.getAdditionalHeaders(),
      })
      .pipe(
        tap((res: any) => JSON.parse(res)),
        catchError(this.handleError)
      );
  }

  getAutoCompleteResult(source: string, request: PortalBaseRequest) {
    return this.http
      .post(source, request, {
        responseType: 'text',
        headers: this.getAdditionalHeaders(),
      })
      .pipe(
        tap((res: any) => JSON.parse(res)),
        catchError(this.handleError)
      );
  }

  getMessages(pageSize: number, page: number, pinnedMessagesIds: number[]) {
    var source =
      environment.apiUrl + '/Portal/Messages-Api/GetPagedMessagesData';
    var request = {
      pageSize,
      page,
      cultureCode: this.getCultureCode(),
      pinnedMessagesIds,
    };
    return this.getDataByToken(source, request);
  }
  postDataWithRequestVerificationToken(
    source: string,
    request: any,
    token: string
  ) {
    let headers = new HttpHeaders({
      RequestVerificationToken: token,
    });
    return this.http.post(source, request, { headers: headers }).pipe(
      tap((res: any) => res),
      catchError(this.handleError)
    );
  }

  private getAdditionalHeaders(): HttpHeaders {
    let headers = new HttpHeaders();

    let preview = this.document
      .querySelector('app-root')
      ?.getAttribute('preview');
    if (preview) {
      headers = new HttpHeaders({ previewpage: 'true' });
      headers = headers.set('culture-code', this.getCultureCode());
    }

    return headers;
  }
}
